// File d'attente des enregistrements du tour faits sans réseau (logements, zones du camping mal couvertes).
//
// Quand un enregistrement du tour échoue pour une raison de réseau (hors ligne, délai dépassé), les actions sont
// rangées ici, dans IndexedDB (photos comprises, en Blob), et l'écran continue avec ce qu'il a saisi. Le service worker
// section « FIELD OUTBOX » de custom-service-worker.js renvoie la file dès que le réseau revient.
//
// Déclenchement du renvoi :
//   - Background Sync (Chrome, Edge, Android) : registration.sync.register, le navigateur réveille le service worker
//     lui-même, même application fermée ;
//   - Firefox, Safari et tout navigateur sans Background Sync : la page demande le renvoi au service worker (message
//     'flush') au retour du réseau (online), au retour sur l'app (visibilitychange, focus, pageshow), à intervalle
//     régulier tant que la file n'est pas vide, et au démarrage de l'app.
// Les deux voies sont toujours utilisées ensemble : le message accélère le renvoi, la synchronisation le garantit.
//
// Un job = les actions d'un tour : { id, tour_id, url, time, upserts, removes, attempts, failed }. Même format que
// save-entries ; une photo non encore envoyée y garde son fichier (photo.file) et sera envoyée par le service worker.

import connexion from '../../../connexion'
import { nowSeconds } from './fieldDates'

const DB_NAME = 'field_outbox'
const STORE = 'jobs'
const SYNC_TAG = 'field-outbox'
const POLL_MS = 15 * 1000

let jobs = []
let dbPromise = null
const listeners = new Set()
const previews = new Map() // identifiant d'action -> { file, url } : adresse d'aperçu d'une photo pas encore envoyée

function openDb()
{
	if (!dbPromise)
		dbPromise = new Promise((resolve, reject) => {
			if (typeof indexedDB === 'undefined')
			{
				reject(new Error('indexeddb'))
				return ;
			}
			const request = indexedDB.open(DB_NAME, 1)
			request.onupgradeneeded = () => {
				if (!request.result.objectStoreNames.contains(STORE))
					request.result.createObjectStore(STORE, { keyPath: 'id' })
			}
			request.onsuccess = () => resolve(request.result)
			request.onerror = () => reject(request.error)
		}).catch(error => {
			dbPromise = null
			throw error
		})
	return dbPromise
}

function run(mode, action)
{
	return openDb().then(db => new Promise((resolve, reject) => {
		const transaction = db.transaction(STORE, mode)
		const request = action(transaction.objectStore(STORE))
		transaction.oncomplete = () => resolve(request ? request.result : null)
		transaction.onerror = () => reject(transaction.error)
		transaction.onabort = () => reject(transaction.error)
	}))
}

function notify()
{
	const summary = outboxSummary()
	listeners.forEach(listener => listener(summary))
}

// pending : jobs en attente d'envoi ; failed : jobs que le serveur a refusés à plusieurs reprises ; reason : dernier échec du renvoi
export function outboxSummary()
{
	const waiting = jobs.filter(job => !job.failed)
	const withError = jobs.find(job => job.last_error)
	return { pending: waiting.length, failed: jobs.length - waiting.length, reason: withError ? withError.last_error : '' }
}

export function subscribeOutbox(listener)
{
	listeners.add(listener)
	return () => listeners.delete(listener)
}

export function hasPending(tourId)
{
	return jobs.some(job => !job.failed && job.tour_id === tourId)
}

// Relit la file dans IndexedDB (au démarrage, et quand le service worker a avancé)
export function loadOutbox()
{
	return run('readonly', store => store.getAll())
		.then(list => {
			jobs = list.sort((a, b) => (a.id < b.id ? -1 : 1))
			notify()
			return jobs
		})
		.catch(() => jobs)
}

// Les photos gardent leur fichier mais perdent les adresses d'aperçu (valables le temps de la page seulement)
function storable(entry)
{
	if (!entry.photo || !entry.photo.file || entry.photo.file_id)
		return entry
	const { url, thumb, ...photo } = entry.photo
	return { ...entry, photo }
}

export function enqueueTourEntries(tourId, upserts, removes)
{
	const job = {
		id: String(Date.now()).padStart(15, '0') + '-' + Math.random().toString(36).slice(2, 8),
		tour_id: tourId,
		url: connexion.connect.url,
		time: nowSeconds(),
		upserts: upserts.map(storable),
		removes,
		attempts: 0,
		failed: false
	}
	return run('readwrite', store => store.put(job)).then(() => {
		jobs = jobs.concat(job)
		notify()
		requestFlush()
		return job
	})
}

function previewOf(entry, photoUrl)
{
	const photo = entry.photo
	if (!photo)
		return entry
	if (photo.file_id)
		return { ...entry, photo: { ...photo, url: photoUrl(photo.file_id, 720), thumb: photoUrl(photo.file_id, 360) } }
	if (!photo.file || typeof URL === 'undefined' || !URL.createObjectURL)
		return entry
	let preview = previews.get(entry.id)
	if (!preview || preview.size !== photo.file.size)
	{
		preview = { size: photo.file.size, url: URL.createObjectURL(photo.file) }
		previews.set(entry.id, preview)
	}
	return { ...entry, photo: { ...photo, url: preview.url, thumb: preview.url } }
}

// Le tour tel que le serveur le renvoie, plus ce qui attend encore d'être envoyé depuis ce téléphone : sans cela, la
// relecture du tour à l'affichage du Plan effacerait les gestes faits hors réseau. photoUrl : adresse d'une photo envoyée.
export function mergePending(tour, photoUrl)
{
	if (!tour || !tour._id)
		return tour
	const mine = jobs.filter(job => !job.failed && job.tour_id === tour._id)
	if (mine.length === 0)
		return tour
	let entries = (tour.entries || []).slice()
	let last = tour.last_activity_time || 0
	mine.forEach(job => {
		const removed = new Set(job.removes || [])
		entries = entries.filter(entry => !removed.has(entry.id))
		job.upserts.forEach(entry => {
			const shown = previewOf(entry, photoUrl)
			const index = entries.findIndex(item => item.id === entry.id)
			if (index === -1)
				entries.push(shown)
			else
				entries[index] = shown
		})
		last = Math.max(last, job.time || 0)
	})
	return { ...tour, entries, last_activity_time: last }
}

// Erreurs qui disent « pas de réseau » (et non « le serveur a refusé ») : l'enregistrement est mis en file
export function isNetworkError(error)
{
	if (!error)
		return false
	// SyntaxError : réponse qui n'est pas du JSON (page d'erreur d'une passerelle sans réseau en amont)
	return error.message === 'offline' || error.name === 'TypeError' || error.name === 'AbortError' || error.name === 'SyntaxError'
}

// Demande le renvoi de la file : synchronisation en arrière-plan quand le navigateur la connaît, et message au
// service worker pour qu'il renvoie tout de suite. Résout false sans service worker (la file reste en attente).
export function requestFlush()
{
	if (typeof navigator === 'undefined' || !navigator.serviceWorker)
		return Promise.resolve(false)
	return navigator.serviceWorker.getRegistration()
		.then(registration => {
			if (!registration || !registration.active)
				return false
			if (registration.sync && registration.sync.register)
				registration.sync.register(SYNC_TAG).catch(() => null)
			registration.active.postMessage('flush')
			return true
		})
		.catch(() => false)
}

// À lancer une fois au démarrage de l'app terrain ; renvoie la fonction qui arrête tout
export function startOutbox()
{
	if (typeof window === 'undefined')
		return () => null
	const flushIfPending = () => {
		if (jobs.some(job => !job.failed))
			requestFlush()
	}
	const onVisible = () => {
		if (document.visibilityState === 'visible')
			flushIfPending()
	}
	loadOutbox().then(flushIfPending)
	window.addEventListener('online', flushIfPending)
	window.addEventListener('focus', flushIfPending)
	window.addEventListener('pageshow', flushIfPending)
	document.addEventListener('visibilitychange', onVisible)
	const timer = setInterval(flushIfPending, POLL_MS)
	let channel = null
	if (typeof BroadcastChannel !== 'undefined')
	{
		channel = new BroadcastChannel(DB_NAME)
		channel.onmessage = () => loadOutbox()
	}
	return () => {
		window.removeEventListener('online', flushIfPending)
		window.removeEventListener('focus', flushIfPending)
		window.removeEventListener('pageshow', flushIfPending)
		document.removeEventListener('visibilitychange', onVisible)
		clearInterval(timer)
		if (channel)
			channel.close()
	}
}
